import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useRouter } from 'src/routes/hooks';

import { useCanAccessRoute } from 'src/hooks/use-permissions';

import { getRedirectRoute, logRouteAnalysis } from 'src/utils/auto-redirect';

import { useAuthContext } from 'src/auth/hooks';

import { LoadingScreen } from 'src/components/loading-screen';

// ----------------------------------------------------------------------

/**
 * 🏠 GUARDIA ESPECIAL PARA DASHBOARD PRINCIPAL
 *
 * FUNCIONALIDAD:
 * - Si el usuario PUEDE acceder al dashboard (/medicore) → Lo deja entrar
 * - Si NO puede acceder → Lo redirige a su PRIMERA página permitida
 * - Usuario master → Siempre accede al dashboard
 * - Usuario sin permisos → Redirige a login
 *
 * ⚠️ ULTRA-RESTRICTIVO: Sin excepciones para roles no-master
 */
const DashboardGuard = ({
  children,
  enableDebugLogs = false,
  showLoadingWhileRedirecting = true,
}) => {
  const { user } = useAuthContext();
  const router = useRouter();
  const canAccessDashboard = useCanAccessRoute('/medicore');

  useEffect(() => {
    if (!user) return;

    const redirectRoute = getRedirectRoute(user);

    if (enableDebugLogs) {
      logRouteAnalysis(user);
    }

    router.replace(redirectRoute);
  }, [user, canAccessDashboard, router, enableDebugLogs]);

  if (!user || (!canAccessDashboard && showLoadingWhileRedirecting)) {
    return <LoadingScreen />;
  }

  if (canAccessDashboard) {
    return children;
  }

  return <LoadingScreen />;
};

DashboardGuard.propTypes = {
  children: PropTypes.node.isRequired,
  enableDebugLogs: PropTypes.bool,
  showLoadingWhileRedirecting: PropTypes.bool,
};

export default DashboardGuard;

// ----------------------------------------------------------------------

/**
 * 🔄 HOC para protección del dashboard con redirección automática
 * @param {Object} options - Opciones de configuración
 * @returns {Function} - Componente envuelto con protección y redirección
 */
export const withDashboardGuard =
  (options = {}) =>
  (WrappedComponent) => {
    const GuardedDashboard = (props) => (
      <DashboardGuard {...options}>
        <WrappedComponent {...props} />
      </DashboardGuard>
    );

    GuardedDashboard.displayName = `withDashboardGuard(${WrappedComponent.displayName || WrappedComponent.name})`;

    return GuardedDashboard;
  };
